/* ═══════════════════════════════════════
   Project SWIFT — cart guard (site-wide, NON-SWIFT pages only)

   While a logged-in customer is on the SWIFT refill page, swift.js keeps their
   live Shopify cart synced with the on-page "Your Refills" selection (a
   "presync"), so clicking CHECKOUT is instant. Those lines are tagged with the
   `_swift_source: swift-refill` line-item property and the cart is hidden on the
   SWIFT page (the header cart icon is disabled there).

   If the customer navigates AWAY from the SWIFT page without checking out, those
   presynced lines must NOT persist in their cart. This guard runs on load of any
   NON-SWIFT page and silently removes them — restoring the cart to whatever the
   customer had before entering the SWIFT experience (their own items are never
   touched by presync, so removing only the SWIFT lines is the restore).

   The one exception: the checkout button sets a `checkout_source: swift` cart
   attribute right before redirecting. A cart carrying that marker was genuinely
   taken to checkout, so we leave it intact (lets an abandoned checkout resume).
   The SWIFT page clears that marker on entry, so re-editing re-arms the guard.

   This file is intentionally self-contained: it loads on every non-SWIFT page,
   where cart.js / cart-drawer.js may be absent, so it must not depend on them.
   pubsub.js (publish) and constants.js (PUB_SUB_EVENTS) ARE global, so it uses
   them when present to let other cart UI react.
   ═══════════════════════════════════════ */

(function () {
  'use strict';

  function cartJsonUrl() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return base.replace(/\/$/, '') + '.js';
  }

  function cartUpdateUrl() {
    if (window.routes && window.routes.cart_update_url) return window.routes.cart_update_url;
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return base.replace(/\/$/, '') + '/update.js';
  }

  function isSwiftLine(item) {
    var props = (item && item.properties) || {};
    return props._swift_source === 'swift-refill' || props._swift_refill === 'true';
  }

  // Reuse the server-rendered bubble; presync only ever decreases the count here,
  // so we just update the number / hide it when it hits zero.
  function updateHeaderCartBubble(count) {
    var wrap = document.getElementById('cart-icon-bubble');
    if (!wrap) return;
    var bubble = wrap.querySelector('.cart-count-bubble');
    if (!bubble) return;
    if (count > 0) {
      bubble.hidden = false;
      var countEl = bubble.querySelector('.cart-item-count');
      if (countEl) countEl.textContent = count < 100 ? String(count) : '';
    } else {
      bubble.hidden = true;
    }
  }

  // Let the theme's own cart UI re-render. <cart-drawer-items> (present on every
  // page via the header) subscribes to this event and, for any source other than
  // 'cart-items', re-fetches the cart-drawer section — refreshing both the drawer
  // contents and the header count. This keeps us off fragile manual DOM surgery.
  function broadcast(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'swift-cart-guard', cartData: cart });
      } catch (_e) {}
    }
  }

  function removeSwiftLines(swiftLines) {
    var updates = {};
    swiftLines.forEach(function (line) {
      if (line && line.key) updates[line.key] = 0;
    });
    if (!Object.keys(updates).length) return;

    fetch(cartUpdateUrl(), {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        // Correct the visible count immediately (theme's broadcast handler does it
        // too, but only after its own section re-fetch — this avoids that lag).
        updateHeaderCartBubble(cart.item_count || 0);
        broadcast(cart);
      })
      .catch(function (err) {
        console.warn('SWIFT guard: failed to remove presynced cart lines', err);
      });
  }

  function run() {
    fetch(cartJsonUrl(), { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;
        var attrs = cart.attributes || {};
        if (attrs.checkout_source === 'swift') return; // committed to checkout — leave intact
        var swiftLines = cart.items.filter(isSwiftLine);
        if (!swiftLines.length) return;
        removeSwiftLines(swiftLines);
      })
      .catch(function (err) {
        console.warn('SWIFT guard: cart check failed', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
